import { useEffect } from 'react';
import { api } from './api/client';
import { lecturesDeLApp, type CeQueMontreLApp } from './lecturesDeLApp';

/**
 * Les lectures de l'app branchées sur la page (chantier journal-des-gestes, ticket 05) : la
 * feuille dit ce qu'elle montre, les signaux partent vers le journal sans que l'écran les attende
 * (`api.signalerUneLecture`). La règle : `lecturesDeLApp.ts`.
 *
 * Quand l'app passe en arrière-plan (`visibilitychange`) ou que l'onglet se ferme (`pagehide`),
 * la fiche et le projet ouverts se ferment ; au retour au premier plan (ou depuis le cache de
 * l'historique, `pageshow`), ils se rouvrent.
 */

const lectures = lecturesDeLApp((signal) => api.signalerUneLecture(signal));
let branchees = false;
/** Combien de feuilles sont montées : une seule à la fois, le plus souvent. */
let montees = 0;

function brancher(): void {
  if (branchees || typeof document === 'undefined') return;
  branchees = true;
  // Une page ouverte derrière un autre onglet : rien ne s'ouvre avant qu'on la regarde.
  if (document.visibilityState === 'hidden') lectures.cacher();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') lectures.cacher();
    else lectures.revenir();
  });
  window.addEventListener('pagehide', () => lectures.cacher());
  window.addEventListener('pageshow', (e) => {
    if (e.persisted && document.visibilityState === 'visible') lectures.revenir();
  });
}

/**
 * Ce que la feuille montre : `null` tant qu'elle n'est pas lue (rien ne change alors). Quand la
 * feuille part, ce qu'elle montrait se ferme.
 */
export function useLecturesDeLApp(montre: CeQueMontreLApp | null): void {
  const { projet, feuille, fiche, angle } = montre ?? {};
  useEffect(() => {
    brancher();
    if (projet === undefined || feuille === undefined || fiche === undefined || angle === undefined) return;
    lectures.montrer({ projet, feuille, fiche, angle });
  }, [projet, feuille, fiche, angle]);
  useEffect(() => {
    montees += 1;
    return () => {
      montees -= 1;
      // Après coup : un démontage suivi d'un remontage immédiat (le double passage de StrictMode
      // en développement) ne ferme rien.
      queueMicrotask(() => {
        if (montees === 0) lectures.montrer(null);
      });
    };
  }, []);
}
